import * as _ from 'underscore';
import { Event, Util } from '..';
import { SelectClickable, SelectClickableCollection } from '../selection';
import { IViewDictionary, IView, ILoadable } from '../../interfaces';
import { IEvent } from '../../interfaces';
import { ViewOptions } from '../../interfaces/options';
import { NavigationItem } from '../navigation';
import { DefaultViewOptions } from '../../const/defaultOptions';

export abstract class View implements IView, ILoadable {
	public activeView: KnockoutObservable<IView>;
	public icon: string;
	public viewId: string;
	public label: string;
	public navigation: SelectClickableCollection<SelectClickable>;
	public parent: IView;
	public views: IViewDictionary;
	public defaultView: IView;
	public isDynamic: boolean;
	public isVirtualView: boolean;
	public template: KnockoutObservable<string>;
	public elementId: string;
    public isVisible: KnockoutObservable<boolean>;
	public isDisabled: KnockoutObservable<boolean>;
	public isFullHeight: boolean;

	private onViewAcitivated: Event<string[]>;
	private onViewDeacitivated: Event<string[]>;
	private onBeforeRoute: Event<string[]>;
	private loadingSelector: string;

	public get ViewActivated(): IEvent<string[]> {
		return this.onViewAcitivated;
	}

	public get ViewDeactivated(): IEvent<string[]> {
		return this.onViewDeacitivated;
	}

	public get BeforeRoute(): IEvent<string[]> {
		return this.onBeforeRoute;
	}

	constructor(options: ViewOptions) {
		this.appStart(options);
	}

	public appStart(options: ViewOptions) {
		_.defaults(options, DefaultViewOptions);
		if (options.viewId.charAt(0) == ':') {
			this.isDynamic = true;
		} else {
			this.isDynamic = false;
		}

		this.isVisible = ko.isObservable(options.visible)
			? options.visible
            : ko.observable<boolean>(options.visible);
        this.isDisabled = ko.isObservable(options.disabled)
            ? options.disabled
            : ko.observable<boolean>(options.disabled);
	    this.viewId = options.viewId;
		this.views = {};
		this.activeView = ko.observable<IView>();
		this.onViewAcitivated = new Event<string[]>();
		this.onViewDeacitivated = new Event<string[]>();
		this.onBeforeRoute = new Event<string[]>();
		this.template = ko.observable<string>(options.template);
		this.navigation = new SelectClickableCollection({
			items: [],
			template: options.navigationLayout
		});
		this.elementId = this.isDynamic ? options.template : this.viewId;
		this.loadingSelector = options.loadingSelector ? options.loadingSelector : '#' + this.elementId;

		this.onViewDeacitivated.subscribe(() => {
			Util.loadingOff();
		});

		//optional
		this.label = options.label;
		this.icon = options.icon;
		this.parent = options.parent;
		this.defaultView = options.defaultView;
		this.isFullHeight = options.isFullHeight;
		this.isVirtualView = false;

		if (options.views && options.views.length > 0) {
			this.initViews(options.views);
		}
	}

	public route(routes: string[]) {
		this.onBeforeRoute.notify(routes);
		var view = this.getView(routes);
		if (view) {
			this.activateView(view, routes);
			if (view.hasViews()) {
				var subRoutes =
					routes && routes.length > 0 ? (view.isVirtualView ? routes : routes.slice(1)) : [];
				view.route(subRoutes);
			}
		}
	}

	public deactivateView(routes: string[]) {
		var view: IView;
		if ((view = this.activeView())) {
			view.deactivated(routes);
			if (view.hasViews()) {
				view.deactivateView(routes);
			}
		}
	}

	public activateView(view: IView, routes: string[]) {
		let _view = this.activeView();
		if (!_view || _view.viewId != view.viewId) {
			this.activeView(view);
		}
		view.activated(routes);
		this.navigation.setSelected(view.viewId);
	}

	public getView(routes: string[]): IView {
		if (!routes || _.isEmpty(routes)) {
			return this.defaultView;
		} else {
			var view: IView = _.find(this.views, (view: IView) => {
				return view.isValidRoute(routes);
			});
			if (view) {
				return view;
			} else {
				return this.routeNotFound(routes);
			}
		}
	}

	public isValidRoute(routes: string[]): boolean {
		return this.isDynamic || (this.isVisible() && this.viewId == routes[0]);
	}

	public initViews(views: IView[]) {
		this.navigation.clear();
		this.views = <IViewDictionary>{};

		views.forEach(view => this.pushView(view));
	}

	public hasViews(): boolean {
		return _.keys(this.views).length > 0;
	}

	public hasParent(): boolean {
		return this.parent != undefined && this.parent != null;
	}

	public getPath(): string {
		var path = this.hasParent() ? this.parent.getPath() : '';
		path = path.concat('/', this.viewId);
		return path;
	}

	public activated(routeSections: string[]): void {
		this.onViewAcitivated.notify();
		var routes =
			routeSections && !_.isEmpty(routeSections) ? routeSections.join(',') : 'DEFAULTVIEW';
		console.info('activated view - ' + this.viewId + ' | routes: ' + routes);
	}

	public deactivated(routeSections: string[]): void {
		this.onViewDeacitivated.notify();
		var routes =
			routeSections && !_.isEmpty(routeSections) ? routeSections.join(',') : 'DEFAULTVIEW';
		console.info('deactivated view - ' + this.viewId + ' | routes: ' + routes);
	}

	public routeNotFound(routeSections: string[]): IView {
		throw new Error(
			'ERROR: view: ' + this.viewId + ' | route not found: ' + routeSections.join('/')
		);
	}

	public pushView(view: IView) {
		var navigationItem: NavigationItem = new NavigationItem({
			id: view.viewId,
			label: view.label,
			icon: view.icon,
			template: null,
			visible: view.isVisible,
			callBack: () => {
				window.router.navigateToView(view);
            },
            disabled: view.isDisabled
		});
		this.navigation.push(navigationItem);
		this.views[view.viewId] = view;
	}

	public loading(state: boolean = true) {
		Util.loading(state, this.loadingSelector);
	}
}
